"use client";

import type React from "react";

import { useState } from "react";
import { useTranslation } from "../../../i18n";
import {
  Database,
  History,
  BarChart3,
  Eye,
  Type,
  Hash,
  ToggleLeft,
  List,
} from "lucide-react";

interface ParameterType {
  id: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  example: string;
  color: string;
}

interface ParameterFeature {
  title: string;
  description: string;
  icon: React.ReactNode;
  benefits: string[];
}

export default function DigitalTwinsParameters() {
  const { t } = useTranslation();
  const [activeType, setActiveType] = useState("string");

  const parameterTypes: ParameterType[] = [
    {
      id: "string",
      name: t("routes.digitalTwins.parameters.types.string.name"),
      icon: <Type className="h-5 w-5" />,
      description: t("routes.digitalTwins.parameters.types.string.description"),
      example: t("routes.digitalTwins.parameters.types.string.example"),
      color: "bg-blue-500",
    },
    {
      id: "float",
      name: t("routes.digitalTwins.parameters.types.float.name"),
      icon: <Hash className="h-5 w-5" />,
      description: t("routes.digitalTwins.parameters.types.float.description"),
      example: t("routes.digitalTwins.parameters.types.float.example"),
      color: "bg-amber-500",
    },
    {
      id: "boolean",
      name: t("routes.digitalTwins.parameters.types.boolean.name"),
      icon: <ToggleLeft className="h-5 w-5" />,
      description: t(
        "routes.digitalTwins.parameters.types.boolean.description"
      ),
      example: t("routes.digitalTwins.parameters.types.boolean.example"),
      color: "bg-purple-500",
    },
    {
      id: "enum",
      name: t("routes.digitalTwins.parameters.types.enum.name"),
      icon: <List className="h-5 w-5" />,
      description: t("routes.digitalTwins.parameters.types.enum.description"),
      example: t("routes.digitalTwins.parameters.types.enum.example"),
      color: "bg-rose-500",
    },
  ];

  const parameterFeatures: ParameterFeature[] = [
    {
      title: t("routes.digitalTwins.parameters.features.visibility.title"),
      description: t(
        "routes.digitalTwins.parameters.features.visibility.description"
      ),
      icon: <Eye className="h-6 w-6" />,
      benefits: [
        t("routes.digitalTwins.parameters.features.visibility.benefits.0"),
        t("routes.digitalTwins.parameters.features.visibility.benefits.1"),
        t("routes.digitalTwins.parameters.features.visibility.benefits.2"),
      ],
    },
    {
      title: t("routes.digitalTwins.parameters.features.history.title"),
      description: t(
        "routes.digitalTwins.parameters.features.history.description"
      ),
      icon: <History className="h-6 w-6" />,
      benefits: [
        t("routes.digitalTwins.parameters.features.history.benefits.0"),
        t("routes.digitalTwins.parameters.features.history.benefits.1"),
        t("routes.digitalTwins.parameters.features.history.benefits.2"),
      ],
    },
    {
      title: t("routes.digitalTwins.parameters.features.analytics.title"),
      description: t(
        "routes.digitalTwins.parameters.features.analytics.description"
      ),
      icon: <BarChart3 className="h-6 w-6" />,
      benefits: [
        t("routes.digitalTwins.parameters.features.analytics.benefits.0"),
        t("routes.digitalTwins.parameters.features.analytics.benefits.1"),
        t("routes.digitalTwins.parameters.features.analytics.benefits.2"),
      ],
    },
  ];

  const activeTypeData =
    parameterTypes.find((type) => type.id === activeType) || parameterTypes[0];

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t("routes.digitalTwins.parameters.title")}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t("routes.digitalTwins.parameters.description")}
          </p>
        </div>

        {/* Parameter Types */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {t("routes.digitalTwins.parameters.supportedTypes")}
          </h3>

          {/* Type Selector */}
          <div className="flex flex-wrap justify-center gap-3 mb-8">
            {parameterTypes.map((type) => (
              <button
                key={type.id}
                onClick={() => setActiveType(type.id)}
                className={`flex items-center px-4 py-2 rounded-lg transition-all duration-200 ${
                  activeType === type.id
                    ? `${type.color} text-white shadow-lg`
                    : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                }`}
              >
                {type.icon}
                <span className="ml-2 font-medium">{type.name}</span>
              </button>
            ))}
          </div>

          {/* Active Type Details */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className={`${activeTypeData.color} p-6 text-white`}>
              <div className="flex items-center mb-4">
                <div className="mr-4 p-2 bg-white/20 rounded-lg">
                  {activeTypeData.icon}
                </div>
                <div>
                  <h4 className="text-2xl font-bold">
                    {activeTypeData.name}{" "}
                    {t("routes.digitalTwins.parameters.title")}
                  </h4>
                  <p className="text-white/90 mt-1">
                    {activeTypeData.description}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6">
              <h5 className="text-lg font-semibold text-gray-900 mb-3">
                {t("routes.digitalTwins.parameters.exampleUsage")}
              </h5>
              <div className="bg-gray-900 p-4 rounded-lg">
                <code className="text-green-400 font-mono">
                  {activeTypeData.example}
                </code>
              </div>
            </div>
          </div>
        </div>

        {/* Parameter Features */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            {t("routes.digitalTwins.parameters.featuresTitle")}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {parameterFeatures.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-4">
                  <div className="p-3 bg-purple-100 rounded-lg mr-4">
                    <div className="text-purple-600">{feature.icon}</div>
                  </div>
                  <h4 className="text-xl font-semibold text-gray-900">
                    {feature.title}
                  </h4>
                </div>

                <p className="text-gray-600 mb-4">{feature.description}</p>

                <ul className="space-y-2">
                  {feature.benefits.map((benefit, benefitIndex) => (
                    <li key={benefitIndex} className="flex items-start">
                      <div className="h-2 w-2 bg-purple-500 rounded-full mr-3 mt-2"></div>
                      <span className="text-sm text-gray-600">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Real-time Dashboard Preview */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
            <h3 className="text-2xl font-bold mb-2">
              {t("routes.digitalTwins.parameters.dashboardTitle")}
            </h3>
            <p className="text-purple-100">
              {t("routes.digitalTwins.parameters.dashboardDescription")}
            </p>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Sample Parameter Cards */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg border-l-4 border-blue-500">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">
                    {t("routes.digitalTwins.parameters.cards.batteryLevel")}
                  </span>
                  <Database className="h-4 w-4 text-blue-500" />
                </div>
                <div className="text-2xl font-bold text-gray-900">87%</div>
                <div className="text-xs text-gray-500">
                  {t("routes.digitalTwins.parameters.cards.lastUpdated", {
                    time: "2 min ago",
                  })}
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg border-l-4 border-green-500">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">
                    {t("routes.digitalTwins.parameters.cards.doorStatus")}
                  </span>
                  <ToggleLeft className="h-4 w-4 text-green-500" />
                </div>
                <div className="text-2xl font-bold text-gray-900">Locked</div>
                <div className="text-xs text-gray-500">
                  {t("routes.digitalTwins.parameters.cards.lastUpdated", {
                    time: "1 min ago",
                  })}
                </div>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-lg border-l-4 border-amber-500">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">
                    {t("routes.digitalTwins.parameters.cards.temperature")}
                  </span>
                  <Hash className="h-4 w-4 text-amber-500" />
                </div>
                <div className="text-2xl font-bold text-gray-900">23.5°C</div>
                <div className="text-xs text-gray-500">
                  {t("routes.digitalTwins.parameters.cards.lastUpdated", {
                    time: "30 sec ago",
                  })}
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg border-l-4 border-purple-500">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-600">
                    {t("routes.digitalTwins.parameters.cards.operationMode")}
                  </span>
                  <List className="h-4 w-4 text-purple-500" />
                </div>
                <div className="text-2xl font-bold text-gray-900">Eco</div>
                <div className="text-xs text-gray-500">
                  {t("routes.digitalTwins.parameters.cards.lastUpdated", {
                    time: "5 min ago",
                  })}
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <button
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
                onClick={() =>
                  (window.location.href =
                    "https://docs.juhuu.app/articles/art_686ea5d89caa5236fbdcbafc")
                }
              >
                {t("routes.digitalTwins.parameters.viewDashboard")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
